import { useEffect, useState, useMemo, useCallback } from 'react';
import { Badge, Space } from 'antd';
import {
  DashboardOutlined,
  SettingOutlined,
  RedditOutlined,
  InboxOutlined,
  RobotOutlined,
  GlobalOutlined,
  TeamOutlined,
  LockOutlined,
  CrownOutlined,
  FileTextOutlined,
  CommentOutlined,
  DatabaseOutlined,
  ShareAltOutlined,
  BulbOutlined,
  DollarOutlined,
  TrophyOutlined,
  WalletOutlined,
  ThunderboltOutlined,
  TagsOutlined,
  WhatsAppOutlined,
  LineChartOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { monitoredSubredditsService } from '../services/monitoredSubredditsService';
import { replyPostsService } from '../services/replyPostsService';
import { api } from '../utils/apiClient';

export function useAppNavMenu() {
  const { user, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [subredditCount, setSubredditCount] = useState(0);
  const [inboxCount, setInboxCount] = useState(0);
  const [featureMatrix, setFeatureMatrix] = useState<Record<string, boolean> | null>(null);
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  const getDefaultOpenKeys = () => {
    const path = location.pathname;
    const keys: string[] = [];

    const llmGeoPaths = [
      '/llm-geo-analysis',
      '/citation-authority',
      '/community-engagement',
      '/structured-data',
      '/cross-platform',
      '/semantic-optimization',
    ];

    if (llmGeoPaths.some((p) => path.startsWith(p))) {
      keys.push('/llm-geo-analysis');
    }

    if (path.startsWith('/inbox') || path.includes('/inbox')) {
      keys.push('/inbox');
    }

    if (path.startsWith('/admin')) {
      keys.push('/admin');
    }

    if (isSuperAdmin && llmGeoPaths.some((p) => path.startsWith(p))) {
      keys.push('user-features');
    }

    return keys;
  };

  useEffect(() => {
    const defaultKeys = getDefaultOpenKeys();
    setOpenKeys((prev) => Array.from(new Set([...prev, ...defaultKeys])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isSuperAdmin]);

  const fetchCount = async () => {
    try {
      const subredditData = await monitoredSubredditsService.getMonitoredSubredditsCount();
      setSubredditCount(subredditData.count);

      const inboxData = await replyPostsService.getPostsWithReplies(100, 0, 'pending');
      setInboxCount(inboxData.length);
    } catch (error) {
      console.error('Error fetching counts:', error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchCount();
    }

    const fetchFeatureMatrix = async () => {
      if (user?.user_type) {
        try {
          const response = await api.get<{ user_type: string; features: Record<string, boolean> }>(
            `/auth/admin/feature-matrix/user/${user.user_type}`
          );
          setFeatureMatrix(response.features);
        } catch (error) {
          console.error('Failed to fetch feature matrix:', error);
          setFeatureMatrix(null);
        }
      }
    };

    fetchFeatureMatrix();

    let interval: NodeJS.Timeout | null = null;
    if (user) {
      interval = setInterval(fetchCount, 10000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [user, user?.user_type]);

  useEffect(() => {
    fetchCount();
  }, [location.pathname]);

  const isFeatureEnabled = useCallback((featureKey: string): boolean => {
    if (featureMatrix === null) {
      if (isSuperAdmin) {
        return featureKey.startsWith('admin_') || featureKey === 'dashboard';
      }
      return (
        featureKey !== 'admin_dashboard' &&
        featureKey !== 'admin_users' &&
        featureKey !== 'admin_sessions'
      );
    }
    return featureMatrix[featureKey] ?? false;
  }, [featureMatrix, isSuperAdmin]);

  const menuItems = useMemo(() => {
    if (isSuperAdmin) {
      const adminMenuItems = [];

      if (isFeatureEnabled('admin_dashboard')) {
        adminMenuItems.push({
          key: '/admin',
          icon: <DashboardOutlined />,
          label: 'Admin Dashboard',
          onClick: () => navigate('/admin'),
        });
      }

      if (isFeatureEnabled('admin_users')) {
        adminMenuItems.push({
          key: '/admin/users',
          icon: <TeamOutlined />,
          label: 'User Management',
          onClick: () => navigate('/admin/users'),
        });
      }

      if (isFeatureEnabled('admin_sessions')) {
        adminMenuItems.push({
          key: '/admin/sessions',
          icon: <LockOutlined />,
          label: 'Sessions',
          onClick: () => navigate('/admin/sessions'),
        });
      }

      adminMenuItems.push({
        key: '/admin/feature-matrix',
        icon: <SettingOutlined />,
        label: 'Feature Matrix',
        onClick: () => navigate('/admin/feature-matrix'),
      });

      adminMenuItems.push({
        key: '/admin/ai-credit-usage',
        icon: <DollarOutlined />,
        label: 'AI Credit Usage',
        onClick: () => navigate('/admin/ai-credit-usage'),
      });

      adminMenuItems.push({
        key: '/admin/credit-management',
        icon: <WalletOutlined />,
        label: 'Credit Management',
        onClick: () => navigate('/admin/credit-management'),
      });

      adminMenuItems.push({
        key: '/admin/pricing-plans',
        icon: <TagsOutlined />,
        label: 'Pricing Plans',
        onClick: () => navigate('/admin/pricing-plans'),
      });

      adminMenuItems.push({
        key: '/admin/action-pricing',
        icon: <ThunderboltOutlined />,
        label: 'Action Pricing',
        onClick: () => navigate('/admin/action-pricing'),
      });

      adminMenuItems.push({
        key: '/admin/supervisors',
        icon: <TeamOutlined />,
        label: 'Supervisors',
        onClick: () => navigate('/admin/supervisors'),
      });

      adminMenuItems.push({
        key: '/admin/competitor-analysis-settings',
        icon: <TrophyOutlined />,
        label: 'CA Settings',
        onClick: () => navigate('/admin/competitor-analysis-settings'),
      });

      adminMenuItems.push({
        key: '/admin/engagement-tracking',
        icon: <LineChartOutlined />,
        label: 'Engagement Tracking',
        onClick: () => navigate('/admin/engagement-tracking'),
      });

      adminMenuItems.push({
        key: '/admin/whatsapp-config',
        icon: <WhatsAppOutlined />,
        label: 'WhatsApp Config',
        onClick: () => navigate('/admin/whatsapp-config'),
      });

      adminMenuItems.push({
        key: '/admin/whatsapp-templates',
        icon: <CommentOutlined />,
        label: 'WhatsApp Templates',
        onClick: () => navigate('/admin/whatsapp-templates'),
      });

      adminMenuItems.push({
        key: '/admin/reddit-generic-scraper',
        icon: <DatabaseOutlined />,
        label: 'Reddit Data Scraper',
        onClick: () => navigate('/admin/reddit-generic-scraper'),
      });

      if (
        adminMenuItems.length > 0 &&
        (isFeatureEnabled('dashboard') ||
          isFeatureEnabled('llm_geo_analysis') ||
          isFeatureEnabled('ai_reply_generator'))
      ) {
        adminMenuItems.push({
          key: 'divider-1',
          type: 'divider' as const,
        });
      }

      const userFeatures = [];
      if (isFeatureEnabled('dashboard')) {
        userFeatures.push({
          key: '/dashboard-view',
          icon: <DashboardOutlined />,
          label: 'User Dashboard',
          onClick: () => navigate('/dashboard'),
        });
      }
      if (isFeatureEnabled('llm_geo_analysis')) {
        if (isFeatureEnabled('llm_geo_full_analysis')) {
          userFeatures.push({
            key: '/llm-geo-analysis',
            icon: <GlobalOutlined />,
            label: 'Full Analysis',
            onClick: () => navigate('/llm-geo-analysis'),
          });
        }
        if (isFeatureEnabled('llm_geo_citation_authority')) {
          userFeatures.push({
            key: '/citation-authority',
            icon: <FileTextOutlined />,
            label: 'Citation Authority',
            onClick: () => navigate('/citation-authority'),
          });
        }
        if (isFeatureEnabled('llm_geo_community_engagement')) {
          userFeatures.push({
            key: '/community-engagement',
            icon: <CommentOutlined />,
            label: 'Community Engagement',
            onClick: () => navigate('/community-engagement'),
          });
        }
        if (isFeatureEnabled('llm_geo_structured_data')) {
          userFeatures.push({
            key: '/structured-data',
            icon: <DatabaseOutlined />,
            label: 'Structured Data',
            onClick: () => navigate('/structured-data'),
          });
        }
        if (isFeatureEnabled('llm_geo_cross_platform')) {
          userFeatures.push({
            key: '/cross-platform',
            icon: <ShareAltOutlined />,
            label: 'Cross-Platform',
            onClick: () => navigate('/cross-platform'),
          });
        }
        if (isFeatureEnabled('llm_geo_semantic_optimization')) {
          userFeatures.push({
            key: '/semantic-optimization',
            icon: <BulbOutlined />,
            label: 'Semantic Optimization',
            onClick: () => navigate('/semantic-optimization'),
          });
        }
      }
      if (isFeatureEnabled('ai_reply_generator')) {
        userFeatures.push({
          key: '/ai-reply-generator',
          icon: <RobotOutlined />,
          label: 'AI Reply Generator',
          onClick: () => navigate('/ai-reply-generator'),
        });
      }

      if (userFeatures.length > 0) {
        adminMenuItems.push({
          key: 'user-features',
          icon: <CrownOutlined className="text-amber-500" />,
          label: <span className="text-gray-500">View User Features</span>,
          children: userFeatures,
        });
      }

      return adminMenuItems;
    }

    const items = [];

    if (isFeatureEnabled('dashboard')) {
      items.push({
        key: '/dashboard',
        icon: <DashboardOutlined />,
        label: 'Dashboard',
        onClick: () => navigate('/dashboard'),
      });
    }

    if (isFeatureEnabled('llm_geo_analysis')) {
      const llmGeoChildren = [];

      if (isFeatureEnabled('llm_geo_full_analysis')) {
        llmGeoChildren.push({
          key: '/llm-geo-analysis/full',
          icon: <GlobalOutlined />,
          label: 'Full Analysis',
          onClick: () => navigate('/llm-geo-analysis'),
        });
      }
      if (isFeatureEnabled('llm_geo_citation_authority')) {
        llmGeoChildren.push({
          key: '/citation-authority',
          icon: <FileTextOutlined />,
          label: 'Citation Authority',
          onClick: () => navigate('/citation-authority'),
        });
      }
      if (isFeatureEnabled('llm_geo_community_engagement')) {
        llmGeoChildren.push({
          key: '/community-engagement',
          icon: <CommentOutlined />,
          label: 'Community Engagement',
          onClick: () => navigate('/community-engagement'),
        });
      }
      if (isFeatureEnabled('llm_geo_structured_data')) {
        llmGeoChildren.push({
          key: '/structured-data',
          icon: <DatabaseOutlined />,
          label: 'Structured Data',
          onClick: () => navigate('/structured-data'),
        });
      }
      if (isFeatureEnabled('llm_geo_cross_platform')) {
        llmGeoChildren.push({
          key: '/cross-platform',
          icon: <ShareAltOutlined />,
          label: 'Cross-Platform',
          onClick: () => navigate('/cross-platform'),
        });
      }
      if (isFeatureEnabled('llm_geo_semantic_optimization')) {
        llmGeoChildren.push({
          key: '/semantic-optimization',
          icon: <BulbOutlined />,
          label: 'Semantic Optimization',
          onClick: () => navigate('/semantic-optimization'),
        });
      }
      if (isFeatureEnabled('competitor_analysis')) {
        llmGeoChildren.push({
          key: '/competitor-analysis',
          icon: <TrophyOutlined />,
          label: 'Competitor Analysis',
          onClick: () => navigate('/competitor-analysis'),
        });
      }

      if (llmGeoChildren.length > 0) {
        items.push({
          key: '/llm-geo-analysis',
          icon: <GlobalOutlined />,
          label: 'LLM Geo Analysis',
          children: llmGeoChildren,
        });
      }
    }

    if (isFeatureEnabled('ai_reply_generator')) {
      items.push({
        key: '/ai-reply-generator',
        icon: <RobotOutlined />,
        label: 'AI Reply Generator',
        onClick: () => navigate('/ai-reply-generator'),
      });
    }

    if (isFeatureEnabled('my_subreddits') && subredditCount > 0) {
      items.push({
        key: '/my-subreddits',
        icon: (
          <Badge count={subredditCount} size="small">
            <RedditOutlined />
          </Badge>
        ),
        label: 'My Subreddits',
        onClick: () => navigate('/my-subreddits'),
      });
    }

    if (isFeatureEnabled('inbox')) {
      const inboxChildren = [];

      if (isFeatureEnabled('inbox_unread')) {
        inboxChildren.push({
          key: '/inbox/unread',
          label: (
            <Space>
              <span>Unread</span>
              {inboxCount > 0 && (
                <Badge count={inboxCount} size="small" style={{ backgroundColor: '#0284c7' }} />
              )}
            </Space>
          ),
          onClick: () => navigate('/inbox/unread'),
        });
      }

      if (isFeatureEnabled('inbox_all')) {
        inboxChildren.push({
          key: '/inbox/all',
          label: 'All Posts',
          onClick: () => navigate('/inbox/all'),
        });
      }

      if (isFeatureEnabled('inbox_saved')) {
        inboxChildren.push({
          key: '/inbox/saved',
          label: 'Saved for Later',
          onClick: () => navigate('/inbox/saved'),
        });
      }

      if (isFeatureEnabled('inbox_replied')) {
        inboxChildren.push({
          key: '/inbox/replied',
          label: 'Replies Generated',
          onClick: () => navigate('/inbox/replied'),
        });
      }

      if (inboxChildren.length > 0) {
        items.push({
          key: '/inbox',
          icon: (
            <Badge count={inboxCount} size="small">
              <InboxOutlined />
            </Badge>
          ),
          label: 'Inbox',
          children: inboxChildren,
        });
      }
    }

    return items;
  }, [
    isSuperAdmin,
    isFeatureEnabled,
    subredditCount,
    inboxCount,
    navigate,
  ]);

  return {
    menuItems,
    openKeys,
    setOpenKeys,
    selectedKey: location.pathname,
  };
}
